import { APIComponentAttributes, ProvenanceSource } from '@ardoq/api-types';
import { toIds } from '@ardoq/core';
import { GetContextMenuOptionsArguments } from '@ardoq/context-menu';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { checkHasWriteAccess } from './utils';
import { isTruthy } from '@ardoq/common-helpers';
import { TrackedContextMenus, trackContextMenuOptionClick } from './tracking';
import {
  addComponentToScenario,
  getAddChildComponentMenuItem,
  getComponentNavigateToMenuItem,
  getFilterMenuItem,
} from './menuItems';
import { hasScenarioFeature } from '@ardoq/features';
import { PermissionContext } from '@ardoq/access-control';
import { type I18nLocale } from '@ardoq/i18n';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';

type GetScenarioContextComponentContextMenuArgs = {
  components: APIComponentAttributes[];
  workspaceAndModel: WorkspaceAndModelLookup;
  isViewpointMode: boolean;
  allowsContextNavigation: boolean;
  canHaveChildren: boolean;
  parent: APIComponentAttributes | null;
  permissionContext: PermissionContext;
  i18nLocale: I18nLocale;
  provenanceSource?: ProvenanceSource;
} & Pick<GetContextMenuOptionsArguments, 'target'>;
export const getScenarioContextComponentContextMenu = ({
  components,
  workspaceAndModel,
  target,
  isViewpointMode,
  allowsContextNavigation,
  canHaveChildren,
  parent,
  permissionContext,
  i18nLocale,
  provenanceSource,
}: GetScenarioContextComponentContextMenuArgs): DropdownItem[] | null => {
  if (components.length !== 1) {
    return null;
  }
  const [component] = components;
  const componentIds = toIds(components);

  const scenarioContextComponentContextTrackingFn = (optionTitle: string) =>
    trackContextMenuOptionClick({
      clickedElement: target,
      contextMenuNamespace:
        TrackedContextMenus.SCENARIO_CONTEXT_COMPONENT_CONTEXT_MENU,
      selectedOptionTitle: optionTitle,
    });

  const hasWriteAccess = checkHasWriteAccess({
    workspaceIds: [component.rootWorkspace],
    permissionContext,
    components,
    referenceSourceComponentsById: {},
  });
  const isComponentLocked = lockOperations.isLocked(
    component.lock,
    getFeatureContext()
  );
  const showEditingMenuItems =
    hasWriteAccess && canHaveChildren && !isComponentLocked;

  return [
    ...(showEditingMenuItems && hasScenarioFeature()
      ? [
          getAddChildComponentMenuItem({
            component,
            canHaveChildren,
            trackingFn: scenarioContextComponentContextTrackingFn,
            isViewpointMode,
            i18nLocale,
            provenanceSource,
          }),
          addComponentToScenario(
            componentIds,
            scenarioContextComponentContextTrackingFn,
            i18nLocale
          ),
        ]
      : []),

    !isViewpointMode &&
      allowsContextNavigation &&
      getComponentNavigateToMenuItem({
        component,
        parent,
        trackingFn: scenarioContextComponentContextTrackingFn,
        i18nLocale,
      }),
    !isViewpointMode &&
      getFilterMenuItem({
        target: { component, workspaceAndModel },
        trackingFunction: scenarioContextComponentContextTrackingFn,
        isScenarioContextComponent: true,
        i18nLocale,
        isViewpointMode,
      }),
  ].filter(isTruthy);
};
